import { assertValue, typedBoolean } from '@shared/helpers'
import { IfpEmploymentType, IfpOccupation, IfpWorkingHours } from '@shared/sec-loans'
import { NameBase } from '@ui-common/components/NameBase'
import { YesNo } from '@ui-common/types'
import { formatFinnishCurrencyValue, getEditLabel } from '@ui-common/utils'
import { StepSummary, StepSummaryView, SummaryData } from '@ui-components/form/StepSummaryView'
import { getIndexedLabel } from '@ui-components/utilities/textHelpers'
import { useMemo } from 'react'
import { IntlShape, useIntl } from 'react-intl'

import { isFormDataValid } from '../../../wizard/wizardData'
import { IfpWizardType } from '../../wizard/wizardMachine'
import { useIfpFormContext } from '../formContext'
import { getIfpApplicantInfoLocalizedEnums, wizardDataToFormData } from './applicantInfoStepUtils'
import { createFormValidator } from './formValidator'
import { ApplicantInfoFormData } from './types'

export const ApplicantInfoSummary = ({
  index,
  data,
  numberOfApplicants,
  onEditRequest,
}: {
  index: number
  data: ApplicantInfoFormData
  numberOfApplicants: number
  onEditRequest?: (index: number) => void
}) => {
  const { wizardType, loan } = useIfpFormContext()
  const isLongWizardType = wizardType === IfpWizardType.Long
  const intl = useIntl()
  const { occupation } = data

  const applicant = assertValue(
    loan.debtors.find((debtor) => debtor.uuid === data.uuid),
    'loan.debtors.find((debtor) => debtor.uuid === data.uuid)',
  )

  const {
    occupationLabels,
    yesNoTexts,
    incomeInfoOtherIncomeLabelText,
    expenseInfoCarOwnershipLabelTexts,
    expenseInfoChildAgeLabelText,
  } = getIfpApplicantInfoLocalizedEnums(intl.formatMessage)

  const occupationSummaryDataMap: Partial<Record<IfpOccupation, () => SummaryData[]>> = {
    [IfpOccupation.Pensioner]: () => getPensionerSummaryData(data, intl),
    [IfpOccupation.Employed]: () => getEmployedSummaryData(data, intl),
    [IfpOccupation.Furlough]: () => getFurloughSummaryData(data, intl),
    [IfpOccupation.Entrepreneur]: () => getEntrepreneurSummaryData(data, intl),
    [IfpOccupation.LongtermSickLeave]: () => getAbsenceOfLeaveSummaryData(data, intl),
    [IfpOccupation.Unemployed]: () => getUnemployedSummaryData(data, intl),
    [IfpOccupation.OnFamilyLeave]: () => getAbsenceOfLeaveSummaryData(data, intl),
    [IfpOccupation.StudyLeave]: () => getAbsenceOfLeaveSummaryData(data, intl),
  }
  const occupationSummaryDataFn = occupationSummaryDataMap[occupation as IfpOccupation]

  const summaryData: SummaryData[] = [
    {
      heading: intl.formatMessage({ id: 'common-cards-contact-info-name-label' }),
      value: <NameBase firstNames={applicant.name} kind="plain" />,
    },
    {
      heading: intl.formatMessage({ id: 'common-phone-number' }),
      value: data.phoneNumber,
    },
    ...(isLongWizardType
      ? [
          {
            heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-everyday-life-label' }),
            value: occupation && occupationLabels[occupation],
            childData: occupationSummaryDataFn?.(),
          },
          {
            heading: intl.formatMessage({ id: 'sec-loans-income-info-has-other-income-form-label' }),
            value: data.hasOtherIncome && yesNoTexts[data.hasOtherIncome],
            childData:
              data.hasOtherIncome === YesNo.Yes
                ? data.otherIncomes?.map((otherIncome, i) => ({
                    heading: getIndexedLabel(
                      intl.formatMessage({ id: 'sec-loans-additional-child-info-label' }),
                      i,
                      assertValue(data.otherIncomes, 'data.otherIncomes').length,
                    ),
                    value: '',
                    hideValueArea: true,
                    childData: [
                      {
                        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-other-income-type-label' }),
                        value: otherIncome.incomeType && incomeInfoOtherIncomeLabelText[otherIncome.incomeType],
                      },
                      {
                        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-income-after-taxes-label' }),
                        value: otherIncome.amountAfterTaxes && formatFinnishCurrencyValue(otherIncome.amountAfterTaxes),
                      },
                    ],
                  }))
                : undefined,
          },
          {
            heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-all-savings-label' }),
            value: data.totalSavings && formatFinnishCurrencyValue(data.totalSavings),
          },
          {
            heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-own-share-of-housing-expenses-label' }),
            value: data.ownShareOfHousingExpenses && formatFinnishCurrencyValue(data.ownShareOfHousingExpenses),
          },
          {
            heading: intl.formatMessage({ id: 'sec-loans-expenses-info-has-car-ownership-form-label' }),
            value: data.hasCarOwnership && yesNoTexts[data.hasCarOwnership],
            childData:
              data.hasCarOwnership === YesNo.Yes
                ? [
                    {
                      heading: intl.formatMessage({ id: 'sec-loans-expenses-info-car-ownership-form-label' }),
                      value: data.carOwnershipType && expenseInfoCarOwnershipLabelTexts[data.carOwnershipType],
                    },
                  ]
                : undefined,
          },
          {
            heading: intl.formatMessage({ id: 'sec-loans-expenses-info-has-alimony-form-label' }),
            value: data.isPayingChildSupport && yesNoTexts[data.isPayingChildSupport],
            childData:
              data.isPayingChildSupport === YesNo.Yes
                ? data.childSupports?.map((childSupport, i) => ({
                    heading: getIndexedLabel(
                      intl.formatMessage({ id: 'sec-loans-additional-child-info-label' }),
                      i,
                      assertValue(data.childSupports, 'data.childSupports').length,
                    ),
                    value: '',
                    hideValueArea: true,
                    childData: [
                      {
                        heading: intl.formatMessage({ id: 'sec-loans-expenses-alimony-child-age-form-label' }),
                        value: childSupport.ageGroup && expenseInfoChildAgeLabelText[childSupport.ageGroup],
                      },
                      {
                        heading: intl.formatMessage({ id: 'sec-loans-expenses-alimony-monthly-cost-label' }),
                        value: childSupport.supportAmount && formatFinnishCurrencyValue(childSupport.supportAmount),
                      },
                    ],
                  }))
                : undefined,
          },
          {
            heading: intl.formatMessage({ id: 'ifp-loans-total-loans-amount-label' }),
            value: data.totalLoansAmount && formatFinnishCurrencyValue(data.totalLoansAmount),
          },
          {
            heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-total-amount-of-loan-repayments-per-month' }),
            value:
              data.totalAmountOfLoanRepaymentsPerMonth &&
              formatFinnishCurrencyValue(data.totalAmountOfLoanRepaymentsPerMonth),
          },
          {
            heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-paying-company-loan-label' }),
            value: data.payingHousingCompanyLoan && yesNoTexts[data.payingHousingCompanyLoan],
            childData:
              data.payingHousingCompanyLoan === YesNo.Yes
                ? [
                    {
                      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-total-housing-company-loan' }),
                      value: data.totalHousingCompanyLoan && formatFinnishCurrencyValue(data.totalHousingCompanyLoan),
                    },
                  ]
                : undefined,
          },
        ]
      : []),
  ]

  const heading = getIndexedLabel(
    intl.formatMessage({ id: 'ifp-loans-applicant-info-step-heading' }),
    index,
    numberOfApplicants,
  )
  const error = useIsApplicantInfoDataComplete(data, !isLongWizardType)
    ? undefined
    : intl.formatMessage(
        { id: 'common-dependant-info-changed' },
        {
          parentSection: intl.formatMessage({ id: 'ifp-loans-info-step-heading' }),
          section: heading,
        },
      )

  const summary: StepSummary = {
    heading,
    error,
    data: summaryData,
    editItemProperties: onEditRequest && {
      onClick: () => onEditRequest(index),
      label: getEditLabel(intl),
    },
  }

  return <StepSummaryView {...summary} />
}

const useIsApplicantInfoDataComplete = (data: ApplicantInfoFormData | undefined, isShortFlow: boolean) => {
  return useMemo(
    () =>
      data &&
      isFormDataValid(wizardDataToFormData(data), (formatMessage) =>
        createFormValidator({
          formatMessage,
          isShortFlow,
          isAsyncValidationEnabled: false,
        }),
      ),
    [data, isShortFlow],
  )
}

const getUnemployedSummaryData = (data: ApplicantInfoFormData, intl: IntlShape): SummaryData[] => {
  const { yesNoTexts, timeUntilEmployedAgainEstimationLabels } = getIfpApplicantInfoLocalizedEnums(intl.formatMessage)

  return [
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-current-income-before-taxes-label' }),
      value: data.currentIncomeBeforeTaxes && formatFinnishCurrencyValue(data.currentIncomeBeforeTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-current-income-after-taxes-label' }),
      value: data.currentIncomeAfterTaxes && formatFinnishCurrencyValue(data.currentIncomeAfterTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-expects-to-be-employed-again-label' }),
      value: data.expectsToBeEmployedAgain && yesNoTexts[data.expectsToBeEmployedAgain],
      childData:
        data.expectsToBeEmployedAgain === YesNo.Yes
          ? [
              {
                heading: intl.formatMessage({
                  id: 'ifp-loans-applicant-info-time-until-employed-again-estimation-label',
                }),
                value:
                  data.timeUntilEmployedAgainEstimation &&
                  timeUntilEmployedAgainEstimationLabels[data.timeUntilEmployedAgainEstimation],
              },
            ]
          : undefined,
    },
  ]
}

const getAbsenceOfLeaveSummaryData = (data: ApplicantInfoFormData, intl: IntlShape): SummaryData[] => {
  const { yesNoTexts, timeUntilEmployedAgainEstimationLabels, employmentTypeLabels, workingHoursLabels } =
    getIfpApplicantInfoLocalizedEnums(intl.formatMessage)

  const childDataWhenHasJobToReturnTo = (employmentType: IfpEmploymentType, workingHours: IfpWorkingHours) => {
    const baseSummary = [
      {
        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-estimated-return-to-work-date' }),
        value: data.estimatedReturnToWorkDate,
      },
      {
        heading: intl.formatMessage({ id: 'employment-info-employment-type-label' }),
        value: employmentTypeLabels[employmentType],
      },
      {
        heading: intl.formatMessage({ id: 'employment-info-employer-label' }),
        value: data.employer,
      },
      {
        heading: intl.formatMessage({ id: 'sec-loans-employment-info-working-hours-form-label' }),
        value: workingHoursLabels[workingHours],
      },
      {
        heading: intl.formatMessage({ id: 'common-employment-info-profession-label' }),
        value: data.profession,
      },
      {
        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-employment-start-date-label' }),
        value: data.employmentStartDate,
      },
    ]

    if (data.employmentType === IfpEmploymentType.TimeLimited) {
      baseSummary.push({
        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-employment-end-date-label' }),
        value: data.employmentEndDate,
      })
    }

    baseSummary.push(
      {
        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-normal-gross-salary-label' }),
        value: data.normalGrossSalary && formatFinnishCurrencyValue(data.normalGrossSalary),
      },
      {
        heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-normal-net-salary-label' }),
        value: data.normalNetSalary && formatFinnishCurrencyValue(data.normalNetSalary),
      },
    )

    return baseSummary
  }

  return [
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-current-income-before-taxes-label' }),
      value: data.currentIncomeBeforeTaxes && formatFinnishCurrencyValue(data.currentIncomeBeforeTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-current-income-after-taxes-label' }),
      value: data.currentIncomeAfterTaxes && formatFinnishCurrencyValue(data.currentIncomeAfterTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-has-job-to-return-to-label' }),
      value: data.hasJobToReturnTo && yesNoTexts[data.hasJobToReturnTo],
      childData:
        data.hasJobToReturnTo === YesNo.Yes
          ? childDataWhenHasJobToReturnTo(
              assertValue(data.employmentType, 'data.employmentType'),
              assertValue(data.workingHours, 'data.workingHours'),
            )
          : [
              {
                heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-expects-to-be-employed-again-label' }),
                // TODO: Use first person form here for "No"?
                value: data.expectsToBeEmployedAgain && yesNoTexts[data.expectsToBeEmployedAgain],
                childData:
                  data.expectsToBeEmployedAgain === YesNo.Yes
                    ? [
                        {
                          heading: intl.formatMessage({
                            id: 'ifp-loans-applicant-info-time-until-employed-again-estimation-label',
                          }),
                          value:
                            data.timeUntilEmployedAgainEstimation &&
                            timeUntilEmployedAgainEstimationLabels[data.timeUntilEmployedAgainEstimation],
                        },
                      ]
                    : undefined,
              },
            ],
    },
  ]
}

const getEntrepreneurSummaryData = (data: ApplicantInfoFormData, intl: IntlShape): SummaryData[] => {
  const legalFormOfTheCompany = assertValue(data.legalFormOfTheCompany, 'data.legalFormOfTheCompany')
  const { companyLegalFormLabels } = getIfpApplicantInfoLocalizedEnums(intl.formatMessage)

  return [
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-company-name-label' }),
      value: data.companyName,
    },
    { heading: intl.formatMessage({ id: 'common-business-id-label' }), value: data.businessId },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-income-before-taxes-label' }),
      value: data.incomeBeforeTaxes && formatFinnishCurrencyValue(data.incomeBeforeTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-income-after-taxes-label' }),
      value: data.incomeAfterTaxes && formatFinnishCurrencyValue(data.incomeAfterTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'sec-loans-entrepreneur-info-company-legal-form' }),
      value: companyLegalFormLabels[legalFormOfTheCompany],
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-company-foundation-date-label' }),
      value: data.companyFoundingDate,
    },
  ]
}

const getPensionerSummaryData = (data: ApplicantInfoFormData, intl: IntlShape): SummaryData[] => {
  return [
    {
      heading: intl.formatMessage({ id: 'sec-loans-income-info-gross-salary-for-pensioners-form-label' }),
      value: data.grossSalary && formatFinnishCurrencyValue(data.grossSalary),
    },
    {
      heading: intl.formatMessage({ id: 'sec-loans-income-info-net-salary-for-pensioners-form-label' }),
      value: data.netSalary && formatFinnishCurrencyValue(data.netSalary),
    },
  ]
}

const getEmployedSummaryData = (data: ApplicantInfoFormData, intl: IntlShape): SummaryData[] => {
  const { employmentTypeLabels, workingHoursLabels } = getIfpApplicantInfoLocalizedEnums(intl.formatMessage)
  const employmentType = assertValue(data.employmentType, 'data.employmentType')
  const workingHours = assertValue(data.workingHours, 'data.workingHours')

  return [
    {
      heading: intl.formatMessage({ id: 'employment-info-employment-type-label' }),
      value: employmentTypeLabels[employmentType],
    },
    {
      heading: intl.formatMessage({ id: 'employment-info-employer-label' }),
      value: data.employer,
    },
    {
      heading: intl.formatMessage({ id: 'sec-loans-employment-info-working-hours-form-label' }),
      value: workingHoursLabels[workingHours],
    },
    {
      heading: intl.formatMessage({ id: 'common-employment-info-profession-label' }),
      value: data.profession,
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-employment-start-date-label' }),
      value: data.employmentStartDate,
    },
    data.employmentType === IfpEmploymentType.TimeLimited && {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-employment-end-date-label' }),
      value: data.employmentEndDate,
    },
    {
      heading: intl.formatMessage({ id: 'sec-loans-income-info-gross-salary-form-label' }),
      value: data.grossSalary && formatFinnishCurrencyValue(data.grossSalary),
    },
    {
      heading: intl.formatMessage({ id: 'sec-loans-income-info-net-salary-form-label' }),
      value: data.netSalary && formatFinnishCurrencyValue(data.netSalary),
    },
  ].filter(typedBoolean)
}

const getFurloughSummaryData = (data: ApplicantInfoFormData, intl: IntlShape): SummaryData[] => {
  const { employmentTypeLabels, workingHoursLabels } = getIfpApplicantInfoLocalizedEnums(intl.formatMessage)
  const employmentType = assertValue(data.employmentType, 'data.employmentType')
  const workingHours = assertValue(data.workingHours, 'data.workingHours')
  const baseSummary = [
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-estimated-furlough-end-date-label' }),
      value: data.estimatedFurloughEndDate,
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-current-income-before-taxes-label' }),
      value: data.currentIncomeBeforeTaxes && formatFinnishCurrencyValue(data.currentIncomeBeforeTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-current-income-after-taxes-label' }),
      value: data.currentIncomeAfterTaxes && formatFinnishCurrencyValue(data.currentIncomeAfterTaxes),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-normal-gross-salary-label' }),
      value: data.normalGrossSalary && formatFinnishCurrencyValue(data.normalGrossSalary),
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-normal-net-salary-label' }),
      value: data.normalNetSalary && formatFinnishCurrencyValue(data.normalNetSalary),
    },
    {
      heading: intl.formatMessage({ id: 'employment-info-employment-type-label' }),
      value: employmentTypeLabels[employmentType],
    },
    {
      heading: intl.formatMessage({ id: 'employment-info-employer-label' }),
      value: data.employer,
    },
    {
      heading: intl.formatMessage({ id: 'sec-loans-employment-info-working-hours-form-label' }),
      value: workingHoursLabels[workingHours],
    },
    {
      heading: intl.formatMessage({ id: 'common-employment-info-profession-label' }),
      value: data.profession,
    },
    {
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-employment-start-date-label' }),
      value: data.employmentStartDate,
    },
  ]

  if (data.employmentType === IfpEmploymentType.TimeLimited) {
    baseSummary.push({
      heading: intl.formatMessage({ id: 'ifp-loans-applicant-info-employment-end-date-label' }),
      value: data.employmentEndDate,
    })
  }

  return baseSummary
}
